import ClearOutlinedIcon from "@mui/icons-material/ClearOutlined";
import { Checkbox, Chip } from "@mui/material";
import classNames from "classnames";
import AutocompleteFormComponent from "components/shared/FormComponent/AutocompleteFormComponent";
import { t } from "i18next";
import type { ChangeEvent, CSSProperties, HTMLAttributes, ReactNode, SetStateAction } from "react";
import { useEffect, useState } from "react";
import type { TFetchStatus } from "store/types";

import variables from "../../../assets/styles/_variables.module.scss";
import StyledTextField from "../StyledTextField/StyledTextField";
import styles from "./style.module.scss";

export type FilterOption = {
    id: number | string;
    name: string;
    icon?: React.ElementType;
    description?: string;
    relatedOptionKey?: "chosenTitles" | "chosenChannels" | "chosenCountryMarkets";
    relatedOptionKeyId?: "titleId" | "channelId" | "countryMarket";
    value?: number | string;
    label?: string;
};
/**
 * Filters autocomplete, used in Analytics page
 */
interface IFiltersAutocomplete<T extends FilterOption> {
    label: string | ReactNode;
    placeholder: string;
    /**
     * All filters options
     */
    filtersOptions: T[];
    /**
     * Selected options
     */
    chosenOptions: T[];
    setChosenOptions?: React.Dispatch<SetStateAction<T[]>>;
    /**
     * Custom function to choose option
     */
    handleChooseOption?: (value: any) => void;
    /**
     * Custom unselect option
     */
    unselectOption?: (optionId: number) => void;
    /**
     * Limit number of selected options. default -1
     */
    optionsLimit?: number;
    style?: CSSProperties;
    size?: "small" | "medium";
    /**
     * On change query value input
     */
    onInputChange?: (e: ChangeEvent<HTMLInputElement>) => void;
    /**
     * On scroll end action
     */
    onScrollEnd?: () => void;
    /**
     * Loading state for on scroll end
     */
    onScrollEndLoading?: TFetchStatus | undefined;
    required?: boolean;
    /**
     * Message to display if error on field.
     */
    errorMessage?: string;
    errorCondition?: boolean;
    /**
     * Limit the nnumber of displayed selected tags
     */
    limitTags?: number;
    /**
     * On blur event
     */
    onBlur?: () => void;
    /**
     * OnClose list event
     */
    onClose?: () => void;
    /**
     * Disable close popup list on select item
     */
    disableCloseOnSelect?: boolean;
    /**
     * Show Option All, to select all options
     */
    showOptionAll?: boolean;
    /**
     * Custom name for option all. Default "Tous"
     */
    optionAllName?: string;
    /**
     * Custom matcher function
     */
    matcher?: (options: T[], state: any) => T[];
}

const FiltersAutocomplete = <T extends FilterOption>({
    label,
    placeholder,
    filtersOptions,
    chosenOptions,
    setChosenOptions,
    handleChooseOption,
    unselectOption,
    optionsLimit = -1,
    style,
    size = "medium",
    onInputChange,
    onScrollEnd,
    onScrollEndLoading,
    required,
    errorMessage,
    errorCondition,
    limitTags = -1,
    onBlur,
    onClose,
    disableCloseOnSelect = false,
    showOptionAll = false,
    optionAllName = t("Analytics.All") ?? "",
    matcher,
}: IFiltersAutocomplete<T>) => {
    const hasOptions = !!chosenOptions.length;
    const [displayInput, setDisplayInput] = useState<"block" | "none">(hasOptions ? "none" : "block");
    const selectedAllDefaultState = chosenOptions.some((opt) => opt.id === "all");
    const [selectedAll, setSelectedAll] = useState(selectedAllDefaultState);

    const renderFiltersOption = (props: HTMLAttributes<HTMLLIElement>, option: any) => {
        const hasLimitReached = chosenOptions.length === optionsLimit;
        /* eslint-disable react/prop-types */
        let isSelected = props["aria-selected"];
        if (option.id === "all") isSelected = selectedAll;
        return (
            <li
                {...props}
                className={classNames({
                    [styles["filters-autocomplete-option-list-item"]]: true,
                    [styles["disabled"]]: hasLimitReached,
                    [styles["selected"]]: isSelected,
                })}
                key={option.name}
            >
                <Checkbox checked={!!isSelected} className={styles["item-checkbox"]} />
                <span style={{ fontSize: "11px" }}>{option.name}</span>
            </li>
        );
    };

    const handleDeleteTag = (optionId: number) => {
        if (selectedAll) {
            setChosenOptions && setChosenOptions([]);
            handleChooseOption && handleChooseOption([]);
            setSelectedAll(false);
            return;
        }
        setChosenOptions && setChosenOptions((prev) => prev.filter((title) => title.id !== optionId));
        unselectOption && unselectOption(optionId);
    };

    const renderTags = (options: any, getTagProps: any) => {
        if (selectedAll) options = options.filter((opt) => opt.id === "all");
        return options.map((option: any, index: number) => {
            return (
                <Chip
                    key={index}
                    title={option.name}
                    {...getTagProps({ index })}
                    className={styles["filters-mini-chip"]}
                    label={option.name}
                    deleteIcon={<ClearOutlinedIcon sx={{ height: "14px", width: "14px" }} />}
                    onDelete={() => handleDeleteTag(option.id)}
                    sx={{
                        background: "var(--backgroundZ2)",
                        fontSize: "11px",
                        color: variables["foreground"],
                        marginRight: "0.25rem",
                        borderRadius: "4px",
                        height: "20px",
                        marginBlock: "2px",
                        maxWidth: "80%",
                    }}
                />
            );
        });
    };

    const handleMouseDown = (e: any) => {
        e.stopPropagation();
        setDisplayInput("block");
    };

    const handleOnBlur = () => {
        if (hasOptions) setDisplayInput("none");
    };

    const renderInput = (params: any) => (
        <StyledTextField
            {...params}
            onChange={onInputChange}
            placeholder={!hasOptions ? placeholder : ""}
            error={errorCondition}
            helperText={errorCondition && (errorMessage ?? "Ce champ est obligatoire.")}
            name="filters"
        />
    );

    const handleChange = (e: any, values: T[]) => {
        const uncheckedOptionAll = selectedAll && !values.some((opt) => opt.id === "all");
        const checkedOptionAll = values.at(-1)?.id === "all";
        // If unchecked option "all"
        if (uncheckedOptionAll) {
            setSelectedAll(false);
            values = [];
        }
        // If last added option is "all"
        if (checkedOptionAll) {
            setSelectedAll(true);
            values = filtersOptions;
        }
        // If all option were selected, uncheck option "all"
        if (selectedAll) {
            values = values.filter((opt) => opt.id !== "all");
            setSelectedAll(false);
        }
        setChosenOptions && setChosenOptions(values);
        handleChooseOption && handleChooseOption(values);
    };

    if (showOptionAll) {
        filtersOptions = [{ id: "all", name: optionAllName } as T, ...filtersOptions];
    }

    return (
        <div onMouseDown={handleMouseDown} style={style}>
            <AutocompleteFormComponent
                onClose={onClose}
                label={label}
                options={filtersOptions}
                filterOptions={matcher}
                placeholder={!hasOptions ? placeholder : ""}
                value={chosenOptions}
                onChange={handleChange}
                renderOption={renderFiltersOption}
                renderOptionText={(option) => option.name}
                multiple
                limitTags={limitTags}
                renderTags={renderTags}
                defaultFilterOptions
                onBlur={onBlur ?? handleOnBlur}
                size={size}
                renderInput={renderInput}
                onScrollEnd={onScrollEnd}
                onScrollEndLoading={onScrollEndLoading}
                error={errorCondition}
                required={required}
                disableCloseOnSelect={disableCloseOnSelect}
                className={classNames({
                    [styles["filters-autocomplete"]]: true,
                    [styles["small"]]: size === "small",
                    [styles["medium"]]: size === "medium",
                })}
                sx={{
                    ".MuiInputBase-input": { display: displayInput },
                }}
            />
        </div>
    );
};

export default FiltersAutocomplete;
